import Graphic from "@arcgis/core/Graphic";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { AppDispatch } from "../services/store";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import MapView from "@arcgis/core/views/MapView";
import { isPolygon, reorderContours } from "./master-plan-helper";
import {
  getGeometryType,
  getSymbolByGeometryType,
  highlightObject,
  resetObjectStyle,
} from "./helpers-for-2d-map";
import { debounce } from "lodash";
import { editFeaturesOnMap } from "../services/actions/master-plan-modification";
import * as projection from "@arcgis/core/geometry/projection";
import { v4 as uuidv4 } from "uuid";
import { planarArea } from "@arcgis/core/geometry/geometryEngine";
import { LandPlotsCoordinatesBlock } from "../services/reducers/master-plan-modification";
import { MiddlewareAPI } from "redux";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Polyline from "@arcgis/core/geometry/Polyline";
import { ConvertedGeometry } from "../services/reducers/map";

export type SimpleFillSymbolProperties =
  Partial<__esri.SimpleFillSymbolProperties>;
export type SimpleMarkerSymbolProperties =
  Partial<__esri.SimpleMarkerSymbolProperties>;
export type SimpleLineSymbolProperties =
  Partial<__esri.SimpleLineSymbolProperties>;
type GraphicHit = __esri.GraphicHit;
type ViewClickEvent = __esri.ViewClickEvent;

export let lastSelectedGraphic: Graphic | null = null;
const originalSymbols = new WeakMap<Graphic, __esri.Symbol>();

export const DrawVerticesLabels = (
  geometry: Polygon | Polyline,
  uuid: string,
  hide = false,
  verticesLabelTempLayer: GraphicsLayer,
  coordIndexMap?: Map<string, number>,
) => {
  const geometryType = geometry.type;

  verticesLabelTempLayer.removeMany(
    Array.from(
      verticesLabelTempLayer.graphics.filter(
        (g) =>
          g.attributes?.uuid === uuid &&
          g.attributes?.geometryType === geometryType,
      ),
    ),
  );

  if (hide) {
    return;
  }
  let vertices: number[][] = [];

  if (geometry.type === "polygon") {
    vertices = geometry.rings.flat();
  } else if (geometry.type === "polyline") {
    vertices = geometry.paths.flat();
  } else {
    return;
  }
  const seenPoints = new Set();
  let drawIndex = 1;

  vertices.forEach((vertex) => {
    const key = `${vertex[0]}-${vertex[1]}`;

    if (!seenPoints.has(key)) {
      seenPoints.add(key);
      const index = coordIndexMap?.get(key) ?? drawIndex;

      const duplicateLabelGraphics = Array.from(
        verticesLabelTempLayer.graphics.filter(
          (graphic) =>
            graphic.attributes?.uuid === uuid &&
            graphic.attributes?.idx === index,
        ),
      );

      if (duplicateLabelGraphics.length) {
        verticesLabelTempLayer.removeMany(duplicateLabelGraphics);
      }

      const point = new Point({
        x: vertex[0],
        y: vertex[1],
        spatialReference: geometry.spatialReference,
      });

      const textSymbol = new TextSymbol({
        text: String(index),
        color: "#362044",
        font: { size: 22, family: "Arial", weight: "bolder" },
        yoffset: 10,
      });

      const textGraphic = new Graphic({
        geometry: point,
        symbol: textSymbol,
        attributes: { uuid, geometryType: geometryType, idx: index },
      });

      verticesLabelTempLayer.add(textGraphic);

      if (!coordIndexMap) drawIndex++;
    }
  });
};

export const DrawVertices = (
  geometry: Polygon | Polyline,
  uuid: string,
  hide = false,
  verticesTempLayer: GraphicsLayer,
) => {
  const geometryType = geometry.type;

  verticesTempLayer.removeMany(
    Array.from(
      verticesTempLayer.graphics.filter(
        (g) =>
          g.attributes?.uuid === uuid &&
          g.attributes?.geometryType === geometryType,
      ),
    ),
  );

  if (hide) {
    return;
  }
  let vertices: number[][] = [];

  if (geometry.type === "polygon") {
    vertices = geometry.rings.flat();
  } else if (geometry.type === "polyline") {
    vertices = geometry.paths.flat();
  } else {
    return;
  }

  const seenPoints = new Set();

  if (!vertices || vertices.length === 0) return;

  vertices.forEach((vertex) => {
    const key = `${vertex[0]}-${vertex[1]}`;

    if (!seenPoints.has(key)) {
      seenPoints.add(key);

      const point = new Point({
        x: vertex[0],
        y: vertex[1],
        spatialReference: geometry.spatialReference,
      });

      const pointSymbol = new SimpleMarkerSymbol({
        color: "#2E1A47",
        size: 10,
        outline: {
          color: "#FFFFFF",
          width: 3,
        },
      });

      const pointGraphic = new Graphic({
        geometry: point,
        symbol: pointSymbol,
        attributes: { uuid, geometryType: geometryType },
      });

      verticesTempLayer.add(pointGraphic);
    }
  });
};

export const handleFeatureClickStyle = (
  selectedGraphic: Graphic,
  store: MiddlewareAPI,
  tempLayers: GraphicsLayer | GraphicsLayer[],
  viewer: MapView | null,
  sketchViewModel?: SketchViewModel,
  mainDrawingLayer?: GraphicsLayer,
  noSketchLayers?: GraphicsLayer | GraphicsLayer[],
  customHighlightStyles?: {
    "simple-fill"?: SimpleFillSymbolProperties;
    "simple-line"?: SimpleLineSymbolProperties;
    "simple-marker"?: SimpleMarkerSymbolProperties;
  },
) => {
  const layersArray = Array.isArray(tempLayers) ? tempLayers : [tempLayers];
  const noSketchLayersArray = Array.isArray(noSketchLayers)
    ? noSketchLayers
    : [noSketchLayers];
  const isDrawingActive = store.getState().map.drawingToolsIsActive;

  if (!selectedGraphic) return;

  if (lastSelectedGraphic && lastSelectedGraphic !== selectedGraphic) {
    clearLastSelectedGraphic(tempLayers);
  }

  if (layersArray.includes(selectedGraphic.layer as GraphicsLayer)) {
    const selectedLayer = selectedGraphic.layer as GraphicsLayer;
    const isExcluded = noSketchLayersArray.includes(selectedLayer);

    if (!isExcluded) {
      const maySwitch = !mainDrawingLayer || selectedLayer === mainDrawingLayer;

      if (
        maySwitch &&
        sketchViewModel &&
        sketchViewModel.layer !== selectedLayer
      ) {
        sketchViewModel.cancel();
        sketchViewModel.layer = selectedLayer;
      }

      const wasAlreadySelected = lastSelectedGraphic === selectedGraphic;

      if (sketchViewModel && isDrawingActive && wasAlreadySelected) {
        sketchViewModel.update(selectedGraphic, { tool: "reshape" });
        return;
      }
    }

    if (!originalSymbols.has(selectedGraphic)) {
      const sym = selectedGraphic.symbol;
      if (
        sym instanceof SimpleFillSymbol ||
        sym instanceof SimpleLineSymbol ||
        sym instanceof SimpleMarkerSymbol
      ) {
        originalSymbols.set(selectedGraphic, sym.clone());
      }
    }

    if (selectedGraphic.symbol?.type === "simple-fill") {
      selectedGraphic.symbol = new SimpleFillSymbol({
        color: "#E69F6AB2",
        outline: {
          color: "#2E1A47CC",
          width: 3,
        },
        ...(customHighlightStyles?.["simple-fill"] || {}),
      });
    }

    if (selectedGraphic.symbol.type === "simple-line") {
      selectedGraphic.symbol = new SimpleLineSymbol({
        color: "#E69F6AB2",
        width: 3,
        ...(customHighlightStyles?.["simple-line"] || {}),
      });
    }

    if (selectedGraphic.symbol.type === "simple-marker") {
      selectedGraphic.symbol = new SimpleMarkerSymbol({
        style: "circle",
        color: "#E69F6AB2",
        size: 8,
        ...(customHighlightStyles?.["simple-marker"] || {}),
      });
    }
  } else if (selectedGraphic.layer.type === "feature") {
    const featureLayer = selectedGraphic.layer as FeatureLayer;
    const storeLayer = store
      .getState()
      .map.layers.find((l: { name: string }) => l.name === featureLayer.id);
    const fieldName =
      storeLayer?.style2D?.fieldName ?? featureLayer.objectIdField;
    const fieldValue = selectedGraphic.attributes?.[fieldName];
    highlightObject(fieldValue, storeLayer, viewer, fieldName);
  }

  lastSelectedGraphic = selectedGraphic;
};

export const clearLastSelectedGraphic = (
  tempLayers: GraphicsLayer | GraphicsLayer[],
) => {
  if (lastSelectedGraphic) {
    const layersArray = Array.isArray(tempLayers) ? tempLayers : [tempLayers];

    const layer = lastSelectedGraphic.layer;

    if (layersArray.includes(layer as GraphicsLayer)) {
      const originalRenderer = originalSymbols.get(lastSelectedGraphic);
      if (originalRenderer) {
        lastSelectedGraphic.symbol = originalRenderer;
        originalSymbols.delete(lastSelectedGraphic);
      }
    } else if (layer && layer.type === "feature") {
      resetObjectStyle();
    }

    lastSelectedGraphic = null;
  }
};

export const handleViewerClick = (
  event: ViewClickEvent,
  store: MiddlewareAPI,
  sketchViewModel: SketchViewModel,
  viewer: MapView | null,
  tempLayers: GraphicsLayer | GraphicsLayer[],
  mainDrawingLayer?: GraphicsLayer,
  noSketchLayers?: GraphicsLayer | GraphicsLayer[],
  customHighlightStyles?: {
    "simple-fill"?: SimpleFillSymbolProperties;
    "simple-line"?: SimpleLineSymbolProperties;
    "simple-marker"?: SimpleMarkerSymbolProperties;
  },
) => {
  const layersArray = Array.isArray(tempLayers) ? tempLayers : [tempLayers];

  const isDrawingActive = store.getState().map.drawingToolsIsActive;

  if (
    event.button === 0 &&
    sketchViewModel.state === "active" &&
    isDrawingActive
  ) {
    sketchViewModel.complete();
    return;
  }

  if (event.button === 0) {
    viewer!.hitTest(event).then((response) => {
      const results = response.results ?? [];

      const tempGraphicHit = results.find(
        (hit) =>
          hit.type === "graphic" &&
          layersArray.includes(hit.graphic.layer as GraphicsLayer),
      ) as GraphicHit;
      if (sketchViewModel.state === "ready" && tempGraphicHit) {
        const selectedGraphic = tempGraphicHit.graphic;
        handleFeatureClickStyle(
          selectedGraphic,
          store,
          tempLayers,
          viewer,
          sketchViewModel,
          undefined,
          noSketchLayers,
          customHighlightStyles,
        );
      } else {
        const featureGraphicHit = results.find(
          (hit) =>
            hit.type === "graphic" &&
            hit.graphic.layer?.type === "feature" &&
            !layersArray.includes(hit.graphic.layer as GraphicsLayer),
        ) as GraphicHit;

        if (featureGraphicHit) {
          const graphic = featureGraphicHit.graphic;
          handleFeatureClickStyle(
            graphic,
            store,
            tempLayers,
            viewer,
            sketchViewModel,
            undefined,
            noSketchLayers,
          );
        }
      }
    });
  } else if (event.button === 2 && isDrawingActive) {
    if (mainDrawingLayer && sketchViewModel.layer !== mainDrawingLayer) {
      sketchViewModel.cancel();
      sketchViewModel.layer = mainDrawingLayer;
    }

    if (sketchViewModel.state === "ready") {
      sketchViewModel.create("polygon");
    } else if (sketchViewModel.state === "active") {
      EndCreateGraphic(sketchViewModel);
    }
  }
};

export const EndCreateGraphic = (sketchViewModel: SketchViewModel) => {
  if (
    (sketchViewModel.createGraphic.geometry as Polygon)?.rings.every(
      (ring) => ring.length > 4,
    )
  ) {
    sketchViewModel.complete();
  } else {
    sketchViewModel.cancel();
  }
};

export const handleDeleteKey = (
  event: KeyboardEvent,
  store: MiddlewareAPI,
  tempLayers: GraphicsLayer | GraphicsLayer[],
  verticesTempLayer: GraphicsLayer,
  createdObjects: { objectId: string | number; uuid?: string }[],
  setGeometries: () => void,
  verticesLabelTempLayer?: GraphicsLayer,
  returnWithLayer?: boolean,
  sketchViewModel?: SketchViewModel,
) => {
  if (sketchViewModel && sketchViewModel.state === "active") {
    return;
  }

  if (event.key === "Delete" && store.getState().map.drawingToolsIsActive) {
    if (lastSelectedGraphic) {
      const uuid = lastSelectedGraphic.attributes?.uuid;
      const objectId = lastSelectedGraphic.attributes?.objectId;

      const matchingObject = createdObjects.find(
        (obj) => obj.objectId === objectId,
      );

      if (matchingObject) {
        setGeometries();
      }

      const vertexGraphics = verticesTempLayer.graphics.filter(
        (g) => g.attributes.uuid === uuid,
      );

      if (verticesLabelTempLayer) {
        const labelGraphics = verticesLabelTempLayer.graphics.filter(
          (g) => g.attributes.uuid === uuid,
        );
        verticesLabelTempLayer.removeMany(labelGraphics.toArray());
      }

      verticesTempLayer.removeMany(vertexGraphics.toArray());

      const deleted = lastSelectedGraphic;
      const originalLayer = deleted.layer;

      const layersArray = Array.isArray(tempLayers) ? tempLayers : [tempLayers];
      layersArray.forEach((layer) => layer.remove(lastSelectedGraphic!!));

      lastSelectedGraphic = null;

      return returnWithLayer
        ? { graphic: deleted, layer: originalLayer }
        : deleted;
    }
  }
  return null;
};

export const deleteCreatedObjects = (
  tempLayer: GraphicsLayer,
  verticesTempLayer: GraphicsLayer,
  verticesLabelTempLayer: GraphicsLayer,
  phaseTerritoryLayer: GraphicsLayer,
  polygonsLayer: GraphicsLayer,
  linesLayer: GraphicsLayer,
) => {
  tempLayer.removeAll();
  verticesTempLayer.removeAll();
  verticesLabelTempLayer.removeAll();
  phaseTerritoryLayer.removeAll();
  polygonsLayer.removeAll();
  linesLayer.removeAll();
  lastSelectedGraphic = null;
};

export const EditGraphics = (
  graphics: Graphic[],
  dispatch: AppDispatch,
  verticesLabelTempLayer?: GraphicsLayer,
  idField: string = "objectId",
  coordIndexMap?: Map<string, number>,
) => {
  sendEditing(graphics, dispatch, idField);

  if (verticesLabelTempLayer) {
    graphics.forEach((graphic) => {
      DrawVerticesLabels(
        graphic.geometry as Polygon,
        graphic.attributes.uuid,
        false,
        verticesLabelTempLayer,
        coordIndexMap,
      );
    });
  }
};

const processGraphics = (
  graphics: Graphic[],
  dispatch: AppDispatch,
  idField: string,
) => {
  const result = graphics.filter((graphic) => graphic.attributes?.[idField]);

  if (result.length === 0) return;

  const map = new Map(
    result.map((graphic) => [
      graphic.attributes[idField],
      projection.project(
        graphic.geometry,
        graphic.attributes.spatialReference,
      ) as Polygon,
    ]),
  );

  dispatch(editFeaturesOnMap(map));
};

const sendEditing = debounce(
  (graphics: Graphic[], dispatch: AppDispatch, idField: string) => {
    processGraphics(graphics, dispatch, idField);
  },
  500,
);

export const getGeometry = async (
  plotIdx: number,
  contourIdx: number,
  targetWkid: number | undefined,
  tempLayer: GraphicsLayer,
  coordinatesData: LandPlotsCoordinatesBlock | null,
): Promise<{ uuid: string; updatedData: LandPlotsCoordinatesBlock } | null> => {
  if (!lastSelectedGraphic || !lastSelectedGraphic.geometry) return null;

  await projection.load();

  const originalGeometry = lastSelectedGraphic.geometry as Polygon;
  const projectedGeometry = projection.project(originalGeometry, {
    wkid: targetWkid,
  }) as Polygon;

  const rings = projectedGeometry.rings?.[0] ?? [];

  if (rings.length === 0) return null;

  const points = rings.map((coord, idx) => ({
    index: idx + 1,
    north: coord[0].toString(),
    east: coord[1].toString(),
    id: uuidv4(),
  }));

  const coordinatesTable = structuredClone(coordinatesData);

  if (!coordinatesTable) return null;

  const plot = coordinatesTable[plotIdx];

  plot.contours[contourIdx].points = points;
  plot.contours[contourIdx].area = planarArea(
    projectedGeometry,
    "square-meters",
  ).toFixed(2);
  plot.area = planarArea(projectedGeometry, "square-meters").toFixed(2);
  plot.wkid = projectedGeometry.spatialReference.wkid;

  plot.contours = reorderContours(plot.contours, plot.wkid);

  const uuid = lastSelectedGraphic.attributes.uuid;

  clearLastSelectedGraphic(tempLayer);

  return { uuid, updatedData: coordinatesTable };
};

export function buildVertexIndexMap(rings: number[][][]): Map<string, number> {
  const counts = rings.map((r) => (isPolygon(r) ? r.length - 1 : r.length));

  const totals: number[] = [];
  counts.reduce((sum, cnt, idx) => {
    const newSum = sum + cnt;
    totals[idx] = newSum;
    return newSum;
  }, 0);

  const coordIndex = new Map<string, number>();
  rings.forEach((ring, i) => {
    const start = i === 0 ? 1 : totals[i - 1] + 1;
    for (let j = 0; j < counts[i]; j++) {
      const [x, y] = ring[j];
      coordIndex.set(`${x}-${y}`, start + j);
    }
  });
  return coordIndex;
}

export function buildDefinitionExpression({
  estateIds,
  objectIds,
  objectIdField,
  baseQuery = "1=1",
}: {
  estateIds?: number[];
  objectIds?: number[];
  objectIdField?: string;
  baseQuery?: string;
} = {}): string {
  const expressions: string[] = [];

  if (estateIds && estateIds.length > 0) {
    const estateCondition =
      estateIds.length === 1
        ? `estateEn = ${estateIds[0]}`
        : `estateEn IN (${estateIds.join(", ")})`;
    expressions.push(estateCondition);
  }

  if (objectIds && objectIds.length > 0 && objectIdField) {
    const idsString = objectIds.join(", ");
    expressions.push(`${objectIdField} NOT IN (${idsString})`);
  }

  if (baseQuery) {
    expressions.push(baseQuery);
  }

  return expressions.join(" AND ");
}

export const addConvertedFeaturesToLayer = (
  convertedGeometry: ConvertedGeometry | null,
  graphicLayer: GraphicsLayer,
) => {
  graphicLayer.removeAll();

  if (convertedGeometry) {
    Object.entries(convertedGeometry).forEach(([fileName, fileData]) => {
      fileData.graphics.forEach((item) => {
        const geometry = getGeometryType(JSON.parse(item.geometry));

        const symbol = getSymbolByGeometryType(geometry.type);

        const graphic = new Graphic({
          geometry,
          attributes: item.attributes,
          symbol,
        });
        (graphic as any).layerName = fileName;

        graphicLayer.add(graphic);
      });
    });
  }
};

export async function applyEditToPlotGeometry(
  updatedGraphic: Graphic,
  plots: { objectId: number; newGeometry: Polygon | null }[],
): Promise<Graphic | null> {
  const uuid = updatedGraphic.attributes?.uuid;
  const updatedType = updatedGraphic.geometry?.type;
  const objectId = updatedGraphic.attributes?.objectId;

  if (!uuid || !updatedType) {
    return null;
  }

  const plot =
    plots.length === 1 ? plots[0] : plots.find((p) => p.objectId === objectId);

  if (!plot || !plot.newGeometry || !Array.isArray(plot.newGeometry.rings)) {
    return null;
  }

  const originalRings = [...plot.newGeometry.rings];
  const spatialReference = plot.newGeometry.spatialReference;

  let updatedGeometry = updatedGraphic.geometry;
  if (!updatedGeometry.spatialReference.equals(spatialReference)) {
    updatedGeometry = projection.project(
      updatedGeometry,
      spatialReference,
    ) as Polygon;
  }

  if (updatedType === "polygon") {
    const updatedPolygon = updatedGeometry as Polygon;
    const ringIndices = updatedGraphic.attributes?.ringIndices;
    if (Array.isArray(updatedPolygon.rings) && Array.isArray(ringIndices)) {
      ringIndices.forEach((ringIdx, i) => {
        originalRings[ringIdx] = updatedPolygon.rings[i];
      });
    }
  }

  if (updatedType === "polyline") {
    const updatedPath = (updatedGeometry as Polyline).paths?.[0];
    const ringIndex = updatedGraphic.attributes?.ringIndex;
    if (Array.isArray(updatedPath) && typeof ringIndex === "number") {
      originalRings[ringIndex] = updatedPath;
    }
  }

  const unifiedGeometry = new Polygon({
    rings: originalRings,
    spatialReference,
  });

  return new Graphic({
    geometry: unifiedGeometry,
    symbol: updatedGraphic.symbol,
    attributes: {
      uuid,
      objectId: plot.objectId,
      ringIndices: originalRings.map((_, i) => i),
      spatialReference,
    },
  });
}
